// ============================================================================
// AdminPlayerDossier - the complete evidence record for one player.
// ============================================================================
// Pick any user and pull everything the platform knows about them into one
// reviewable, exportable record: identity and account state, wallet, the
// full financial ledger with transfer counterparties, lifetime play stats,
// automated cheat-scan flags and player reports, registered devices, and
// their recent hands with positions, revealed cards, per-hand net, and the
// complete action log. "Download evidence file" exports the server response
// verbatim as timestamped JSON (generatedAt/generatedBy are stamped
// server-side) so the record can back a formal proceeding.
// ============================================================================
import React, { useState } from 'react';
import {
  Box, Heading, Text, Input, HStack, VStack, Button, Alert, AlertIcon,
  Badge, Code, Avatar, Table, Thead, Tbody, Tr, Th, Td, Divider,
  Stat, StatLabel, StatNumber, StatGroup, Accordion, AccordionItem,
  AccordionButton, AccordionPanel, AccordionIcon, Tag,
} from '@chakra-ui/react';
import { Search, FileDown, Shield, Landmark, Spade } from 'lucide-react';
import gameApiService from '../../services/gameApiService';

interface SearchHit { id: string; username: string; photoURL?: string | null }

const fmtTs = (ms: number | null | undefined): string =>
  ms ? new Date(ms).toLocaleString() : '-';

const fmtAmt = (n: number | null | undefined): string =>
  typeof n === 'number' ? (n >= 0 ? `+${n.toFixed(2)}` : n.toFixed(2)) : '-';

const cardStr = (c: any): string => {
  if (!c?.rank) return '??';
  const suit: Record<string, string> = { hearts: '♥', diamonds: '♦', clubs: '♣', spades: '♠' };
  return `${c.rank}${suit[c.suit] || c.suit || ''}`;
};

const AdminPlayerDossier: React.FC = () => {
  const [search, setSearch] = useState('');
  const [searching, setSearching] = useState(false);
  const [hits, setHits] = useState<SearchHit[]>([]);
  const [selected, setSelected] = useState<SearchHit | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dossier, setDossier] = useState<any | null>(null);

  const doSearch = async () => {
    if (!search.trim()) return;
    setSearching(true);
    setHits([]);
    if (/^[A-Za-z0-9]{20,}$/.test(search.trim())) {
      setHits([{ id: search.trim(), username: '(direct UID)' }]);
      setSearching(false);
      return;
    }
    const r = await gameApiService.searchUsers(search.trim(), 10);
    setSearching(false);
    setHits(r.success && r.users ? r.users : []);
  };

  const loadDossier = async (u: SearchHit) => {
    setSelected(u);
    setDossier(null);
    setError(null);
    setLoading(true);
    const r = await gameApiService.getPlayerDossier(u.id);
    setLoading(false);
    if (!r.success || !r.dossier) { setError(r.error || 'Failed to build dossier'); return; }
    setDossier(r.dossier);
  };

  const downloadEvidence = () => {
    if (!dossier) return;
    const name = dossier.identity?.username || dossier.subjectUserId;
    const blob = new Blob([JSON.stringify(dossier, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `evidence_${name}_${new Date(dossier.generatedAt).toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const id = dossier?.identity;
  const agg = dossier?.hands?.aggregates;
  const stats = dossier?.lifetimeStats;

  return (
    <Box bg="gray.800" border="1px solid" borderColor="gray.700" borderRadius="md" p={5} mt={6}>
      <VStack align="stretch" spacing={4}>
        <HStack>
          <Shield size={18} color="#f59e0b" />
          <Heading size="sm" color="orange.300">Player Evidence Dossier</Heading>
        </HStack>
        <Text fontSize="xs" color="gray.400">
          The complete record on one player - identity, money movement with counterparties,
          play statistics, automated flags, reports, devices, and recent hands with full
          action logs. Export it as a timestamped evidence file for a formal case.
        </Text>

        <HStack>
          <Input value={search} onChange={e => setSearch(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && doSearch()}
            placeholder="Username or paste UID"
            bg="gray.900" borderColor="gray.600" color="white" />
          <Button colorScheme="orange" leftIcon={<Search size={14} />}
            onClick={doSearch} isLoading={searching}>Find</Button>
        </HStack>

        {hits.length > 0 && (
          <VStack align="stretch" spacing={1}>
            {hits.map(u => (
              <HStack key={u.id} px={3} py={2}
                bg={selected?.id === u.id ? 'orange.900' : 'gray.900'}
                borderRadius="sm" cursor="pointer" _hover={{ bg: 'gray.700' }}
                onClick={() => loadDossier(u)}>
                <Avatar size="xs" name={u.username} src={u.photoURL || undefined} />
                <Text fontSize="sm" color="white" flex={1}>{u.username}</Text>
                <Code fontSize="xs" bg="transparent" color="gray.500">{u.id.substring(0, 12)}…</Code>
              </HStack>
            ))}
          </VStack>
        )}

        {loading && <Text color="gray.400" fontSize="sm">Assembling record… (scans every hand they played)</Text>}
        {error && <Alert status="error" bg="red.900" borderRadius="md"><AlertIcon /><Text fontSize="sm">{error}</Text></Alert>}

        {dossier && id && (
          <VStack align="stretch" spacing={4}>
            <Divider borderColor="gray.700" />

            {/* Header + export */}
            <HStack justify="space-between" align="start" flexWrap="wrap" gap={2}>
              <VStack align="start" spacing={1}>
                <HStack>
                  <Heading size="md" color="white">{id.username || dossier.subjectUserId}</Heading>
                  {id.banned && <Badge colorScheme="red">BANNED</Badge>}
                  {id.securityHold?.active && <Badge colorScheme="orange">SECURITY HOLD</Badge>}
                  {id.kycStatus === 'verified' && <Badge colorScheme="green">KYC</Badge>}
                </HStack>
                <Text fontSize="xs" color="gray.500">
                  {id.email || 'no email'} · joined {fmtTs(id.createdAt)} · UID <Code fontSize="xs">{dossier.subjectUserId}</Code>
                </Text>
                <Text fontSize="xs" color="gray.500">
                  Record generated {fmtTs(dossier.generatedAt)} by admin {String(dossier.generatedBy).substring(0, 8)}
                </Text>
              </VStack>
              <Button size="sm" colorScheme="orange" leftIcon={<FileDown size={14} />} onClick={downloadEvidence}>
                Download evidence file
              </Button>
            </HStack>

            {/* Security verdict */}
            <Box bg="gray.900" p={3} borderRadius="md" border="1px solid"
              borderColor={dossier.security?.cheatFlag ? 'orange.600' : 'gray.700'}>
              <Text fontSize="xs" fontWeight="800" color="orange.300" mb={1}>AUTOMATED SCAN</Text>
              {dossier.security?.cheatFlag ? (
                <VStack align="start" spacing={1}>
                  <Text fontSize="sm" color="white">
                    Score: <b>{dossier.security.cheatFlag.score ?? '-'}</b>
                  </Text>
                  {(dossier.security.cheatFlag.reasons || []).map((r: string, i: number) => (
                    <Text key={i} fontSize="xs" color="gray.300">· {r}</Text>
                  ))}
                </VStack>
              ) : (
                <Text fontSize="sm" color="green.300">No automated flags on record.</Text>
              )}
              {dossier.security?.reportsAgainst?.length > 0 && (
                <Text fontSize="xs" color="gray.400" mt={2}>
                  {dossier.security.reportsAgainst.length} player report(s) filed against this account
                  - full detail in the evidence file.
                </Text>
              )}
            </Box>

            {/* Play + money summary */}
            <StatGroup flexWrap="wrap" gap={3}>
              <Stat minW="110px"><StatLabel color="gray.400" fontSize="xs">Hands on record</StatLabel>
                <StatNumber color="cyan.300" fontSize="lg">{dossier.hands?.totalFound ?? 0}</StatNumber></Stat>
              <Stat minW="110px"><StatLabel color="gray.400" fontSize="xs">Net (recent)</StatLabel>
                <StatNumber color={(agg?.netTotal || 0) >= 0 ? 'green.300' : 'red.300'} fontSize="lg">{fmtAmt(agg?.netTotal)}</StatNumber></Stat>
              <Stat minW="110px"><StatLabel color="gray.400" fontSize="xs">Showdown win rate</StatLabel>
                <StatNumber color="white" fontSize="lg">{agg?.showdownWinRate != null ? `${agg.showdownWinRate}%` : '-'}</StatNumber></Stat>
              <Stat minW="110px"><StatLabel color="gray.400" fontSize="xs">VPIP / PFR</StatLabel>
                <StatNumber color="white" fontSize="lg">
                  {stats?.vpip != null ? `${Math.round(stats.vpip)}%` : '-'} / {stats?.pfr != null ? `${Math.round(stats.pfr)}%` : '-'}
                </StatNumber></Stat>
              <Stat minW="110px"><StatLabel color="gray.400" fontSize="xs">PlayCoins / PSS</StatLabel>
                <StatNumber color="white" fontSize="lg">
                  {dossier.wallet ? `${Math.round(dossier.wallet.playCoins)} / $${(dossier.wallet.pkCash || 0).toFixed(2)}` : '-'}
                </StatNumber></Stat>
            </StatGroup>

            <Accordion allowMultiple>
              {/* Ledger */}
              <AccordionItem border="none">
                <AccordionButton px={0}>
                  <HStack flex={1}><Landmark size={14} color="#94a3b8" />
                    <Text fontSize="sm" fontWeight="700" color="gray.200">
                      Financial ledger ({dossier.ledger?.returned || 0} entries)
                    </Text></HStack>
                  <AccordionIcon color="gray.400" />
                </AccordionButton>
                <AccordionPanel px={0}>
                  <HStack flexWrap="wrap" gap={2} mb={3}>
                    {Object.entries(dossier.ledger?.totalsByType || {}).map(([type, v]: [string, any]) => (
                      <Tag key={type} size="sm" colorScheme={v.total >= 0 ? 'green' : 'red'}>
                        {type}: {fmtAmt(v.total)} ({v.count})
                      </Tag>
                    ))}
                  </HStack>
                  {Object.keys(dossier.ledger?.transferCounterparties || {}).length > 0 && (
                    <Box mb={3}>
                      <Text fontSize="xs" fontWeight="800" color="orange.300" mb={1}>TRANSFER COUNTERPARTIES</Text>
                      {Object.entries(dossier.ledger.transferCounterparties).map(([uid, c]: [string, any]) => (
                        <Text key={uid} fontSize="xs" color="gray.300">
                          <Code fontSize="xs">{uid.substring(0, 12)}…</Code> received {c.in.toFixed(2)} · sent {c.out.toFixed(2)} · {c.count} transfer(s)
                        </Text>
                      ))}
                    </Box>
                  )}
                  <Box maxH="300px" overflowY="auto">
                    <Table size="sm" variant="simple">
                      <Thead><Tr>
                        <Th color="gray.500">When</Th><Th color="gray.500">Type</Th>
                        <Th color="gray.500" isNumeric>Amount</Th><Th color="gray.500">Detail</Th>
                      </Tr></Thead>
                      <Tbody>
                        {(dossier.ledger?.transactions || []).map((t: any) => (
                          <Tr key={t.id}>
                            <Td fontSize="xs" color="gray.400" whiteSpace="nowrap">{fmtTs(t.timestamp)}</Td>
                            <Td fontSize="xs" color="gray.300">{t.type}</Td>
                            <Td fontSize="xs" isNumeric color={(t.amount || 0) >= 0 ? 'green.300' : 'red.300'}>{fmtAmt(t.amount)}</Td>
                            <Td fontSize="xs" color="gray.500" maxW="260px" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">{t.description}</Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                </AccordionPanel>
              </AccordionItem>

              {/* Hands */}
              <AccordionItem border="none">
                <AccordionButton px={0}>
                  <HStack flex={1}><Spade size={14} color="#94a3b8" />
                    <Text fontSize="sm" fontWeight="700" color="gray.200">
                      Hand evidence ({dossier.hands?.returned || 0} of {dossier.hands?.totalFound || 0} hands, newest first)
                    </Text></HStack>
                  <AccordionIcon color="gray.400" />
                </AccordionButton>
                <AccordionPanel px={0}>
                  <VStack align="stretch" spacing={2} maxH="420px" overflowY="auto">
                    {(dossier.hands?.hands || []).map((h: any, i: number) => (
                      <Box key={i} bg="gray.900" p={3} borderRadius="sm">
                        <HStack justify="space-between" flexWrap="wrap">
                          <Text fontSize="xs" color="gray.400">
                            {fmtTs(h.completedAt)} · hand #{h.handNumber} · {h.tableId} · pot {h.totalPot}
                            {h.showdown ? ' · showdown' : h.foldWin ? ' · fold win' : ''}
                          </Text>
                          <Text fontSize="xs" fontWeight="800" color={(h.subject?.net || 0) >= 0 ? 'green.300' : 'red.300'}>
                            net {fmtAmt(h.subject?.net)}
                          </Text>
                        </HStack>
                        <Text fontSize="xs" color="gray.300" mt={1}>
                          {h.subject?.position || '?'} ·{' '}
                          {h.subject?.holeCards ? h.subject.holeCards.map(cardStr).join(' ') : 'cards not revealed'}
                          {h.subject?.folded ? ' · folded' : ''}
                          {' · board: '}{(h.communityCards || []).map(cardStr).join(' ') || '-'}
                        </Text>
                        <Text fontSize="xs" color="gray.500" mt={1}>
                          vs {(h.opponents || []).map((o: any) => `${o.name || o.id.substring(0, 8)}${o.isBot ? ' [BOT]' : ''}`).join(', ')}
                        </Text>
                        {(h.actionLog || []).length > 0 && (() => {
                          // Evidence-grade action narrative. Log entries are
                          // compact {p,a,amt,r,t}: resolve names, group by
                          // street, and show SECONDS TAKEN per action - the
                          // timing evidence itself. Subject's rows highlighted.
                          const names: Record<string, string> = {
                            [dossier.subjectUserId]: id?.username || 'this player',
                          };
                          const botIds = new Set<string>();
                          (h.opponents || []).forEach((o: any) => {
                            names[o.id] = o.name || String(o.id).substring(0, 8);
                            if (o.isBot) botIds.add(o.id);
                          });
                          let prevT: number | null = null;
                          let prevR: string | null = null;
                          const lines: React.ReactNode[] = [];
                          h.actionLog.forEach((a: any, j: number) => {
                            const secs = prevT != null && a.t
                              ? Math.round((a.t - prevT) / 100) / 10 : null;
                            if (a.t) prevT = a.t;
                            if (a.r && a.r !== prevR) {
                              prevR = a.r;
                              lines.push(
                                <Text key={`street-${j}`} fontSize="10px" fontWeight="800"
                                  color="cyan.400" mt={1.5} textTransform="uppercase"
                                  letterSpacing="0.08em">{a.r}</Text>
                              );
                            }
                            const isSubject = a.p === dossier.subjectUserId;
                            lines.push(
                              <Text key={j} fontSize="xs"
                                color={isSubject ? 'orange.200' : 'gray.400'}
                                fontWeight={isSubject ? 700 : 400}>
                                {names[a.p] || String(a.p || 'unknown').substring(0, 10)}
                                {botIds.has(a.p) ? ' [BOT]' : ''}
                                {' '}{a.a}{a.amt ? ` ${a.amt}` : ''}
                                {secs != null ? (
                                  <Text as="span" color={isSubject && secs < 1.2 ? 'red.300' : 'gray.600'}>
                                    {' '}· took {secs}s{isSubject && secs < 1.2 ? ' (instant)' : ''}
                                  </Text>
                                ) : null}
                              </Text>
                            );
                          });
                          return (
                            <Box bg="#0a0e14" borderRadius="sm" p={2} mt={2}
                              maxH="200px" overflowY="auto">
                              {lines}
                            </Box>
                          );
                        })()}
                      </Box>
                    ))}
                  </VStack>
                  <Text fontSize="xs" color="gray.500" mt={2}>
                    Full untruncated action logs for every hand are in the evidence file.
                  </Text>
                </AccordionPanel>
              </AccordionItem>

              {/* Devices */}
              <AccordionItem border="none">
                <AccordionButton px={0}>
                  <Text flex={1} textAlign="left" fontSize="sm" fontWeight="700" color="gray.200">
                    Devices ({dossier.devices?.length || 0})
                  </Text>
                  <AccordionIcon color="gray.400" />
                </AccordionButton>
                <AccordionPanel px={0}>
                  {(dossier.devices || []).length === 0
                    ? <Text fontSize="xs" color="gray.500">No registered devices.</Text>
                    : (dossier.devices || []).map((d: any, i: number) => (
                      <Text key={i} fontSize="xs" color="gray.300">
                        {(d.platform || 'unknown').toUpperCase()} · {d.active ? 'active' : 'inactive'} · registered {fmtTs(d.createdAt)}
                      </Text>
                    ))}
                </AccordionPanel>
              </AccordionItem>
            </Accordion>
          </VStack>
        )}
      </VStack>
    </Box>
  );
};

export default AdminPlayerDossier;
